// Payer-addressee resolution for transport-order documents and emails.
// A transport order is a contract with the company that bills us for the leg
// (transaction_transports.commercial_company_id — a spedition when one arranged
// the transport), while CMR / Annex VII always show the physical carrier
// (transport_company_id). This swaps the carrier slots' COMPANY fields to the
// payer while keeping each leg's driver/vehicle/price details.
// Used by BOTH mirrors of applyDefaults (google-api-service/template-pipeline.ts
// and EmailService) — keep it the single implementation.

import { keysToCamelCase as keysToCamelCaseUntyped } from './flattenData.ts';
import { enrichCarrier as enrichCarrierUntyped } from './helpers.ts';

// helpers.ts / flattenData.ts are untyped (any-in/any-out); pin safe signatures once.
const keysToCamelCase = keysToCamelCaseUntyped as (obj: unknown) => unknown;
const enrichCarrier = enrichCarrierUntyped as (transport: unknown, countries?: unknown[]) => unknown;

// Column list mirrors the transport_order data_source's transport_company embed
// (supabase/seed/template_configs.sql) so payer and carrier expose the same fields.
const PAYER_COMPANY_SELECT =
  'id, short_name, full_name, nip, bdo, street, zip_code, city, country_code, payment_terms, ' +
  'contacts(email, full_name, phone, purposes, is_deleted)';

/** CamelCased template-data leg — only the fields this module touches. */
export interface TemplateTransport {
  id?: number | null;
  transportCompany?: unknown;
  [key: string]: unknown;
}

interface PayerLookupRow {
  id: number;
  commercialCompany: unknown;
}

/** Structural client type — satisfied by both supabase-js and the Deno client. */
export interface PayerQueryClient {
  from(table: string): {
    select(columns: string): {
      in(column: string, values: number[]): PromiseLike<{ data: unknown; error: unknown }>;
    };
  };
}

export interface PayerCarrierSlots {
  firstCarrier: unknown;
  secondCarrier: unknown;
  thirdCarrier: unknown;
}

/**
 * Resolve the enriched carrier slots addressed to each leg's PAYER.
 * `sortedTransports` are the camelCased, sequence-sorted legs already present in
 * the template data. Falls back to the physical carrier when a leg has no
 * commercial company or the lookup fails (doc generation must not break).
 */
export async function resolvePayerCarriers(
  sb: PayerQueryClient,
  sortedTransports: ReadonlyArray<TemplateTransport | null | undefined>,
  countries?: unknown[],
): Promise<PayerCarrierSlots> {
  const ids = sortedTransports
    .map(t => t?.id)
    .filter((id): id is number => typeof id === 'number');

  let payerByLegId = new Map<number, unknown>();
  if (ids.length > 0) {
    try {
      const { data, error } = await sb
        .from('transaction_transports')
        .select(`id, commercial_company:commercial_company_id(${PAYER_COMPANY_SELECT})`)
        .in('id', ids);
      if (error) {
        console.warn('[resolvePayerCarriers] payer lookup failed, falling back to carriers:', error);
      } else {
        const rows = keysToCamelCase(data ?? []) as PayerLookupRow[];
        payerByLegId = new Map(
          rows.filter(r => r.commercialCompany != null).map(r => [r.id, r.commercialCompany]),
        );
      }
    } catch (err) {
      console.warn('[resolvePayerCarriers] payer lookup failed, falling back to carriers:', err);
    }
  }

  const slot = (leg: TemplateTransport | null | undefined): unknown => {
    if (!leg) return null;
    const payer = leg.id != null ? payerByLegId.get(leg.id) : undefined;
    return enrichCarrier({ ...leg, transportCompany: payer ?? leg.transportCompany }, countries);
  };

  return {
    firstCarrier: slot(sortedTransports[0]),
    secondCarrier: slot(sortedTransports[1]),
    thirdCarrier: slot(sortedTransports[2]),
  };
}
